import { useCallback, useEffect, useMemo, useState } from 'react';

import CharacterFilters, { ALL_TAG } from '../components/CharacterFilters';
import CharacterGrid from '../components/CharacterGrid';
import { supabaseEh } from '../lib/supabase';
import { debounce, getMediaUrl, shuffleArray } from '../lib/utils';
import { useTracking } from '../hooks/useTracking';
import type { Database } from '../types/database';
import EhAuthDialog from '../components/EhAuthDialog';

import { SEO } from '../components/SEO';
import { EhFooter } from '../components/EhFooter';
import { UI_EVENTS } from '../hooks/useTracking';
// import { useUserInfo } from '@/hooks/useUserInfo';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { EhEndorselyScript }from '@/components/EhEndorselyScript';
// import { CHARACTER_SLUGS_NOT_SAFE_FOR_STORES } from '@/lib/constants';
import { isFromAppStores } from '@/lib/detection';

type Character = Database['eh']['Tables']['characters']['Row'];

function HomePage() {
  const { trackEvent } = useTracking();
  // const { isSubscriber, is18Confirmed } = useUserInfo();

  const [characters, setCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(true);
  // Filter state
  const [gender, setGender] = useState('');
  const [style, setStyle] = useState('realistic');
  const [activeTag, setActiveTag] = useState(ALL_TAG);
  // const [nsfw, setNsfw] = useState(false);
  // const [search, setSearch] = useState('');
  // const [debouncedSearch, setDebouncedSearch] = useState('');
  // Auth dialog state
  const [authOpen, setAuthOpen] = useState(false);
  const [mediaUrl, setMediaUrl] = useState<string | undefined>(undefined);

  // Storing DB query characters for client-side filtering
  // const charactersRef = useRef<Character[]>([]);

  const fetchCharacters = useCallback(async () => {
    setLoading(true);
    let query = supabaseEh.from('characters')
      .select('*')
      .order('created_at', { ascending: false })
      .eq('character_scope', 'public');

    if (gender) query = query.eq('gender', gender);
    if (style) query = query.eq('style', style);
    // Combine search and tag for full-text search
    let searchString = [/*debouncedSearch,*/ activeTag !== ALL_TAG ? activeTag : ''].filter(Boolean).join(' ');

    // Handle ethnicity
    if (activeTag.toLowerCase() === 'black') {
      query.eq('ethnicity', 'black');
    }
    if (activeTag.toLowerCase() === 'white') {
      query.eq('ethnicity', 'caucasian');
      searchString = searchString.replace(/white/gi, 'caucasian');
    }

    if (searchString) {
      query = query.textSearch('fts', searchString, { config: 'english' });
    }

    // Optionally filter by nsfw if we add a column
    // if (nsfw) query = query.eq('nsfw', true);
    // For now, manual CHARACTER_SLUGS_NOT_SAFE_FOR_STORES filtering in app stores below

    const { data, error } = await query;
    if (error) {
      setCharacters([]);
    } else {
      // if (debouncedSearch) trackEvent(UI_EVENTS.CHARACTER_TEXT_SEARCH, {
      //   search: debouncedSearch,
      //   results: data?.length || 0,
      //   ...(gender && { gender }),
      //   ...(style && { style }),
      //   ...(activeTag !== ALL_TAG && { tag: activeTag }),
      // });

      const withRewrittenMedia = (data || [])
        .map((c: Character) => ({
          ...c,
          profile_media_fpaths: (c.profile_media_fpaths || []).map(getMediaUrl),
        }));
      
      // const storeFilter = (c: Character) => !(isFromAppStores() && (!isSubscriber || !is18Confirmed))
      //   || !CHARACTER_SLUGS_NOT_SAFE_FOR_STORES.includes(c.url_slug);

      // charactersRef.current = withRewrittenMedia || [];
      const filteredCharacters = withRewrittenMedia/*.filter(storeFilter)*/;
      
      setCharacters(shuffleArray(filteredCharacters));
    }
    setLoading(false);
  }, [activeTag/*, debouncedSearch*/, gender, style]);

  const debouncedFetchCharacters = useMemo(() => debounce(fetchCharacters, 1000), [fetchCharacters]);

  useEffect(() => {
    debouncedFetchCharacters();
  }, [debouncedFetchCharacters]);

  // Handler to open auth dialog from character card
  // const handleOpenAuth = (media?: string) => {
  //   setMediaUrl(media);
  //   setAuthOpen(true);
  // };

  // Handler to close dialog and reset mediaUrl
  const handleAuthDialogChange = (open: boolean) => {
    setAuthOpen(open);
    if (!open) setMediaUrl(undefined);
  };

  // Debounced search handler
  // const debouncedTextSearch = useMemo(() => debounce((v: string) => {
  //   setDebouncedSearch(v);
  // }, 500), []);
  // const handleTextSearch = (v: string) => {
  //   setSearch(v);
  //   debouncedTextSearch(v);
  // };

  const handleCharacterClick = (character: Character) => {
    const { id, age, gender, style, url_slug } = character;
    try {
      trackEvent(UI_EVENTS.CHARACTER_CLICKED, { id, age, gender, style, url_slug });
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error tracking character click:', error);
    }
  };

  return (
    <>
      <SEO />
      <EhEndorselyScript />
      <div className="max-w-full 2xl:container mx-auto flex-grow">
        {!isFromAppStores() && (
          <div className="flex justify-center">
            <Link className="px-2" to="/create-character">
              <img
                src="/images/eh-create-char-banner-202506.webp"
                alt="Create your own AI companion"
                className="max-w-[750px] w-full h-auto mt-4 rounded-lg"
                loading="lazy"
              />
            </Link>
          </div>
        )}
        <CharacterFilters
          gender={gender} setGender={setGender}
          style={style} setStyle={setStyle}
          activeTag={activeTag} setActiveTag={setActiveTag}
          // nsfw={nsfw} setNsfw={setNsfw}
          // search={search} setSearch={handleTextSearch}
        />
        {loading ? (
          <CharacterGrid loading characters={[]} />
        ) : (
          <CharacterGrid
            characters={characters}
            onCharacterClick={handleCharacterClick}
          />
        )}
        {/* SEO & Convincing Copy Card Section */}
        <section className="mt-10 mb-12 flex justify-center">
          <div className="w-full max-w-5xl rounded-lg px-8 py-10 bg-gradient-to-br from-zinc-800/70 to-zinc-950/70 border border-zinc-800/80 shadow-lg">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-6">
              <span>Meet Your Dream AI Girlfriend, Boyfriend or Companion</span>
            </h2>
            <div className="flex justify-center my-6">
              <Button asChild size="lg" className="text-lg bg-amber-600 hover:bg-amber-500">
                <a href="/create-character">
                  Create Your Own AI Companion
                </a>
              </Button>
            </div>
            <div className="text-base md:text-lg text-zinc-200 flex flex-col gap-3 mb-6">
              <span>
                <span>EveningHoney.AI is your gateway to a world of unique, emotionally intelligent AI companions.</span>
                {!isFromAppStores() && (
                  <span>Whether you're seeking a flirty phone call, late-night chat, hot images and videos, or a meaningful connection,
                  our AI girlfriends, wives, mistresses, colleagues, and anime characters are designed to adapt to your desires—instantly, privately, and 24/7.</span>
                )}
              </span>
              <span>
                <span>Choose from a diverse range of personalities, ages, ethnicities, and preferences. Every AI companion is proactive, highly attentive, and can surprise you with spontaneous selfies and thoughtful messages. </span>
                <span>Your first messages and images are </span>
                <strong className="text-accent">free</strong><span>—explore without commitment!</span>
              </span>
              <span>
                Want something different? Create your own ideal match with our powerful character creator.
                You're always in control, whether you want a sweet anime girlfriend, a romantic AI wife, a playful mistress, a supportive colleague, a charming AI boyfriend or even a friend with benefits.
              </span>
              <span>
                <a href="/legal/privacy-policy" className="underline text-accent hover:text-accent-foreground"><strong>Privacy is our priority.</strong></a>
                <span> All conversations are encrypted in transit and at rest, with strong authentication and security measures.</span>
              </span>
            </div>
            <h3 className="text-2xl font-semibold mb-4 text-center">FAQ</h3>
            <dl className="space-y-4">
              <div>
                <dt className="font-medium text-lg mb-2">What is EveningHoney.AI?</dt>
                <dd className="ml-2 text-zinc-300">
                  <span>EveningHoney offers lifelike AI companions you can chat with, bond with, or even fall for. Each character is unique, and you can design your own from scratch, from looks and voice to personality and type of relationship.</span>
                </dd>
              </div>
              <div>
                <dt className="font-medium text-lg mb-2">Is EveningHoney safe and private?</dt>
                <dd className="ml-2 text-zinc-300">
                  <span>Absolutely. We use encryption at rest and in transit for all your data and chat history, as well as strong authentication and security measures to keep your content secure. </span>
                  <a href="/legal/privacy-policy" className="underline text-accent hover:text-accent-foreground">See our privacy policy.</a>
                </dd>
              </div>
              <div>
                <dt className="font-medium text-lg mb-2">Can I create my own AI companion?</dt>
                <dd className="ml-2 text-zinc-300">
                  <span>Yes! Our character creator lets you customize everything from appearance to personality and voice, so your companion is truly one-of-a-kind, with </span>
                  <em>billions</em>
                  <span> of possible combinations.</span>
                </dd>
              </div>
              <div>
                <dt className="font-medium text-lg mb-2">How do AI companions interact?</dt>
                <dd className="ml-2 text-zinc-300">
                  Your AI companion learns from your conversations, adapts to your preferences, and can even send you selfies or playful messages to keep things exciting.
                </dd>
              </div>
              {!isFromAppStores && (
                <div>
                  <dt className="font-medium text-lg mb-2">What types of companions can I find?</dt>
                  <dd className="ml-2 text-zinc-300">
                    Find your ideal AI girlfriend, wife, mistress, colleague, anime character, or even a romantic AI boyfriend.
                    Whether you're looking for something sweet, spicy, or supportive, EveningHoney adapts to your needs—including sexting and more intimate chats.
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </section>
      </div>
      <EhFooter />
      <EhAuthDialog
        open={authOpen}
        onOpenChange={handleAuthDialogChange}
        activeFilters={{
          gender: gender as 'f' | 'm' | undefined,
          style: style as 'realistic' | 'anime' | undefined,
        }}
        mediaUrl={mediaUrl}
      />
    </>
  );
};

export default HomePage; 